import { Button, ButtonTheme } from '@apply/components';
import { useSaveExperience } from '../../../../hooks/useSaveExperience';
import { GenericAlertPopup } from '@apply/components';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
    useExperienceProjectTypeId,
    useExperienceHomeConstructedAfterPurchase,
    useExperienceVerificationStatusTypeId,
    useExperienceCommercialConsiderationAcknowledged,
    useExperiencePropertyTypeId,
} from '../../../../fields/ExperienceFields';
import { ExperienceProjectType, ExperiencePropertyType, ExperienceVerificationStatusType } from '@apply/constants';
import { isMobileScreen } from '@apply/ui';

interface SubmitButtonProps {
    experienceId: string | undefined;
    validationGroupRef: React.RefObject<any>;
}

export const SubmitButton: React.FC<SubmitButtonProps> = ({ experienceId, validationGroupRef }) => {
    const mobileScreen = isMobileScreen();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const saveExperience = useSaveExperience(experienceId);
    const [projectTypeId] = useExperienceProjectTypeId(experienceId);
    const [verificationStatusType] = useExperienceVerificationStatusTypeId(experienceId);
    const [constructedAfterClosed] = useExperienceHomeConstructedAfterPurchase(experienceId);
    const [propertyType] = useExperiencePropertyTypeId(experienceId);
    const [commercialConsiderationAcknowledged] = useExperienceCommercialConsiderationAcknowledged(experienceId);

    const getSuccessMessage = () =>
        experienceId !== undefined ? 'Your property was updated successfully.' : 'Your property was added successfully.';

    const canSubmit = () => {
        //Don't allow save on unacceptable Experience rules
        //Constructed After Closed / Personal Residence
        if (projectTypeId === ExperienceProjectType.PersonalResidence && constructedAfterClosed !== true) {
            return false;
        }
        //Commercial / Unacknowledged
        if (propertyType === ExperiencePropertyType.Commercial && commercialConsiderationAcknowledged !== true) {
            return false;
        }

        //Don't allow save on experience that's been marked verified, rejected, or unable to verify
        if (verificationStatusType != undefined && verificationStatusType != ExperienceVerificationStatusType.Submitted) {
            return false;
        }

        return true;
    };

    const getFailureMessage = () => {
        return (
            <>
                <div>Unable to save Experience Property.</div>
                <div>Please try again later.</div>
            </>
        );
    };

    const handleSuccessClose = () => {
        queryClient.removeQueries({ queryKey: ['userExperience'] });
        navigate('/experience');
        window.scrollTo(0, 0);
    };

    const handleSubmit = async () => {
        const validationResult = validationGroupRef?.current?.instance?.validate();

        if (!validationResult?.isValid) {
            window.scrollTo(0, 0);
            return;
        }

        const result = await saveExperience();

        if (result?.success) {
            GenericAlertPopup({
                title: 'Success',
                children: getSuccessMessage(),
                width: '350px',
                handleClose: handleSuccessClose,
            });
        } else if ('reason' in result && result.reason === 'duplicate-address') {
            GenericAlertPopup({
                title: 'Duplicate Property',
                children: 'We are unable to accept this property, you have already submitted a property with this address.',
                width: '350px',
            });
        } else {
            GenericAlertPopup({
                title: 'Error',
                children: getFailureMessage(),
                width: '350px',
            });
        }
    };

    return (
        <Button
            id='saveExperienceButton'
            theme={ButtonTheme.Primary}
            width={mobileScreen ? '130px' : '300px'}
            handleClick={handleSubmit}
            disabled={!canSubmit()}
        >
            Submit
        </Button>
    );
};
